import { z } from 'zod';
import { appId, appName, appText, declarationList } from './app-v2-primitives.mjs';
import { storableJson, storableString, validateFieldValue } from './app-v2-values.mjs';
import { richTextDocumentSchema } from './app-richtext.mjs';
import { appMediaDeclarationSchema } from './app-media.mjs';
import { ManagedImageSlot } from '../artifacts/images/values.mjs';
import { parsePresentValue } from '../data-formats/schemas.mjs';

// The type a field's values are stored and read as (app-data/cell/shape.mjs wireType).
const storedType = (type) => ({ enum: 'text', relation: 'text', richtext: 'json' })[type] ?? type;

// A Save writes a field's default into stored rows as it is (a backfill, or
// cleared values), so it must be a value those rows read back: no longer text
// or number than a write may store, and no string a table cannot store.
function storableDefault(field) {
  if (field.default === null) return true;
  try {
    parsePresentValue({ type: storedType(field.type), value: field.default });
  } catch {
    return false;
  }
  return storableJson(field.default);
}

export const fieldDeclarationSchema = z
  .object({
    id: appId,
    name: appName,
    label: appText(200).optional(),
    type: z.enum([
      'text',
      'integer',
      'decimal',
      'boolean',
      'date',
      'timestamp',
      'json',
      'file',
      'richtext',
      'enum',
      'relation',
    ]),
    nullable: z.boolean().default(false),
    required: z.boolean().default(false),
    renamedFrom: appName.optional(),
    enumValues: z
      .array(appText(200).refine(storableString))
      .min(1)
      .max(100)
      .refine((items) => new Set(items).size === items.length)
      .optional(),
    relation: z.object({ collectionId: appId, fieldId: appId }).strict().optional(),
    default: z.json().optional(),
    readPolicyRef: appId.optional(),
    writePolicyRef: appId.optional(),
    media: appMediaDeclarationSchema.optional(),
  })
  .strict()
  .superRefine((field, ctx) => {
    if (field.media && field.type !== 'json')
      ctx.addIssue({
        code: 'custom',
        path: ['media'],
        message: 'Media metadata belongs only to explicit JSON fields',
      });
    for (const [type, key] of [
      ['enum', 'enumValues'],
      ['relation', 'relation'],
    ]) {
      if ((field.type === type) !== (field[key] !== undefined))
        ctx.addIssue({
          code: 'custom',
          path: [key],
          message: `${key} belongs only to ${type} fields`,
        });
    }
    if (
      Object.hasOwn(field, 'default') &&
      (!validateFieldValue(field.default, field, {
        json: (value) => !field.media || ManagedImageSlot.safeParse(value).success,
        richtext: (value) => richTextDocumentSchema.safeParse(value).success,
      }) ||
        !storableDefault(field))
    )
      ctx.addIssue({
        code: 'custom',
        path: ['default'],
        message: 'Default must match the declared field domain',
      });
  });

export const collectionDeclarationSchema = z
  .object({
    id: appId,
    name: appName,
    label: appText(200).optional(),
    fields: declarationList(fieldDeclarationSchema, 200).min(1),
    indexes: z
      .array(
        z
          .object({
            id: appId,
            fieldIds: z.array(appId).min(1).max(8),
            unique: z.boolean().default(false),
          })
          .strict(),
      )
      .max(20)
      .default([]),
    searchFieldIds: z.array(appId).max(20).default([]),
    rowAccessPolicyRef: appId.optional(),
  })
  .strict();

export function validateCollectionReferences(collections, ctx) {
  const byId = new Map(collections.map((collection) => [collection.id, collection]));
  collections.forEach((collection, index) => {
    const fields = new Set(collection.fields.map((field) => field.id));
    const indexed = collection.indexes
      .flatMap((item) => item.fieldIds)
      .concat(collection.searchFieldIds);
    if (indexed.some((id) => !fields.has(id)))
      ctx.addIssue({
        code: 'custom',
        path: ['collections', index],
        message: 'Index/search references an undeclared field',
      });
    if (new Set(collection.indexes.map((item) => item.id)).size !== collection.indexes.length)
      ctx.addIssue({
        code: 'custom',
        path: ['collections', index, 'indexes'],
        message: 'Index IDs must be unique',
      });
    for (const field of collection.fields) {
      if (!field.relation) continue;
      const target = byId.get(field.relation.collectionId);
      if (!target?.fields.some((item) => item.id === field.relation.fieldId))
        ctx.addIssue({
          code: 'custom',
          path: ['collections', index, 'fields'],
          message: 'Relation references an undeclared field',
        });
    }
  });
}
